import { Journeys, Targets, useCrossFlow } from '@atlassiansox/cross-flow-support';
import { captureException } from '@sentry/react';
import React, { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { graphql, usePreloadedQuery } from 'react-relay';

import { useAnalytics } from '@townsquare/analytics';
import { getConfig } from '@townsquare/config';
import { ErrorState } from '@townsquare/error-state';
import { filterUndefined } from '@townsquare/filter-type';
import { LoadingState } from '@townsquare/loading-state';
import { useRelayResource } from '@townsquare/relay-utils';

import { TitanEapCrossFlowProps } from '../types';

import { titanEapCrossFlowQueryResource } from './resource';

const TitanEAPCrossFlowInternals = (props: TitanEapCrossFlowProps): JSX.Element => {
  const crossFlow = useCrossFlow();
  const analytics = useAnalytics();
  const config = getConfig();

  const data = usePreloadedQuery(
    graphql`
      query TitanEapCrossFlowQuery {
        organizationsUserAdministers {
          edges {
            node {
              orgId
            }
          }
        }
      }
    `,
    props.queryRef,
  );
  const adminOrgIds =
    data?.organizationsUserAdministers?.edges?.map(edge => edge?.node?.orgId ?? '').filter(filterUndefined) ?? [];
  const adminOrgsCount = adminOrgIds.length;
  const adminOrgId = adminOrgIds[0];
  const isOrgAdmin = Boolean(adminOrgsCount);

  const handleOptInOrRequestTrial = async () => {
    analytics.ui('button', 'clicked', { screen: props.context, isOrgAdmin, adminOrgsCount }, 'enableTitan');

    if (adminOrgId) {
      // button href with handle linking to AdminHub opt in page in new tab
      analytics.track('admin', 'titanEAPCrossFlow', { screen: props.context, adminOrgsCount });
    } else {
      if (!crossFlow.isEnabled) {
        captureException(new Error('CrossFlow module is not enabled but user tried to use it'), {
          extra: { screen: props.context },
        });
      } else {
        analytics.track('endUser', 'titanEAPCrossFlow', { screen: props.context });
        await crossFlow.api.open({
          journey: Journeys.GET_STARTED,
          sourceComponent: `no-workspace-${props.context}`,
          sourceContext: 'townsquare',
          targetProduct: Targets.ATLAS,
        });
      }
    }
  };

  return props.children({
    isOrgAdmin,
    handleOptInOrRequestTrial,
    orgId: adminOrgId,
    adminButtonHref: adminOrgId ? `${config.adminHubUrl}/o/${adminOrgId}/townsquare` : undefined,
  });
};

export const TitanEAPCrossFlow = (props: Omit<TitanEapCrossFlowProps, 'queryRef'> & { isFullPage: boolean }) => {
  const queryRef = useRelayResource(titanEapCrossFlowQueryResource);

  if (!queryRef) {
    return <LoadingState />;
  }

  return (
    <ErrorBoundary fallback={<ErrorState isFullPage={props.isFullPage} />}>
      <Suspense fallback={<LoadingState />}>
        <TitanEAPCrossFlowInternals context={props.context} children={props.children} queryRef={queryRef} />
      </Suspense>
    </ErrorBoundary>
  );
};
